/**
 * Nuday storefront currency converter (display-only).
 *
 * Detects the visitor's country (IP geolocation → timezone fallback), maps it
 * to a currency, converts AED prices client-side, and re-formats them with the
 * target currency code + localized amount. Re-rendered price nodes (cart
 * drawer, bundle widgets, sticky price box…) are handled via MutationObserver.
 *
 * IMPORTANT: conversion is display-only. Checkout still charges the shop
 * currency (AED); the customer's card/payment provider applies FX.
 * Detected rate source: open.er-api.com (cached 12h in localStorage), with a
 * jsDelivr currency-api fallback and an embedded snapshot as last resort.
 */
(function () {
  "use strict";

  var SHOP_CURRENCY = String(window.NudayShopCurrency || "AED").toUpperCase();
  var LS_COUNTRY = "nuday_geo_country";
  var LS_RATES = "nuday_fx_rates";
  var RATES_TTL = 12 * 60 * 60 * 1000; // 12 hours
  var RATES_URLS = [
    "https://open.er-api.com/v6/latest/" + SHOP_CURRENCY,
    "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/" +
      SHOP_CURRENCY.toLowerCase() +
      ".json",
  ];

  // Snapshot fallback (updated Sat, 05 Sep 2026) — used only when both rate
  // APIs are unreachable. Key: 1 unit of SHOP_CURRENCY (AED) in target.
  var SNAPSHOT_RATES = {"AED":1,"AFN":17.655057,"ALL":21.638058,"AMD":99.068792,"ANG":0.487406,"AOA":253.759067,"ARS":410.449149,"AUD":0.377996,"AWG":0.487406,"AZN":0.462823,"BAM":0.458597,"BBD":0.544588,"BDT":33.453889,"BGN":0.458597,"BHD":0.102383,"BIF":814.951784,"BMD":0.272294,"BND":0.344943,"BOB":3.307205,"BRL":1.391818,"BSD":0.272294,"BTN":25.736579,"BWP":3.727226,"BYN":0.838236,"BZD":0.544588,"CAD":0.376543,"CDF":623.557356,"CHF":0.220519,"CLF":0.006423,"CLP":253.890364,"CNH":1.826718,"CNY":1.832228,"COP":858.518474,"CRC":123.432527,"CUP":6.535058,"CVE":25.854589,"CZK":5.669549,"DJF":48.392376,"DKK":1.751497,"DOP":15.983852,"DZD":36.307337,"EGP":13.871816,"ERN":4.084411,"ETB":44.206255,"EUR":0.234477,"FJD":0.599027,"FKP":0.201392,"FOK":1.751497,"GBP":0.201392,"GEL":0.711518,"GGP":0.201392,"GHS":3.094292,"GIP":0.201392,"GMD":20.310089,"GNF":2392.30885,"GTQ":2.077368,"GYD":56.978047,"HKD":2.135056,"HNL":7.302487,"HRK":1.766666,"HTG":35.602195,"HUF":84.922065,"IDR":4812.584928,"ILS":0.819953,"IMP":0.201392,"INR":25.736585,"IQD":357.003168,"IRR":386981.162769,"ISK":32.993559,"JEP":0.201392,"JMD":43.083633,"JOD":0.193057,"JPY":42.520524,"KES":35.231645,"KGS":23.82671,"KHR":1101.449555,"KID":0.377996,"KMF":115.355045,"KRW":367.322108,"KWD":0.084064,"KYD":0.226912,"KZT":124.103876,"LAK":6044.74278,"LBP":24370.319946,"LKR":89.367543,"LRD":48.243229,"LSL":4.347502,"LYD":1.729801,"MAD":2.545611,"MDL":4.703395,"MGA":1177.238774,"MKD":14.472576,"MMK":571.972183,"MNT":968.679685,"MOP":2.199108,"MRU":10.947413,"MUR":12.733693,"MVR":4.205343,"MWK":475.193048,"MXN":4.600337,"MYR":1.101169,"MZN":17.377502,"NAD":4.347502,"NGN":360.57347,"NIO":10.015316,"NOK":2.532935,"NPR":41.178526,"NZD":0.462987,"OMR":0.104696,"PAB":0.272294,"PEN":0.915162,"PGK":1.214303,"PHP":17.071907,"PKR":75.588127,"PLN":1.010667,"PYG":1607.708476,"QAR":0.99115,"RON":1.231037,"RSD":27.502828,"RUB":23.577346,"RWF":401.957417,"SAR":1.021103,"SBD":2.169648,"SCR":3.910113,"SDG":138.934631,"SEK":2.604555,"SGD":0.344943,"SHP":0.201392,"SLE":6.728123,"SLL":6728.122844,"SOS":155.645591,"SRD":10.334433,"SSP":1538.82314,"STN":5.744683,"SYP":33.176526,"SZL":4.347502,"THB":8.962422,"TJS":2.514572,"TMT":0.953148,"TND":0.791978,"TOP":0.643453,"TRY":13.183554,"TTD":1.833532,"TVD":0.377996,"TWD":8.612665,"TZS":719.282352,"UAH":12.144008,"UGX":1012.087907,"USD":0.272294,"UYU":10.954632,"UZS":3210.873885,"VES":221.575521,"VND":7086.485918,"VUV":32.184309,"WST":0.732639,"XAF":153.806727,"XCD":0.735194,"XCG":0.487406,"XDR":0.198564,"XOF":153.806727,"XPF":27.980591,"YER":64.506926,"ZAR":4.347509,"ZMW":5.203631,"ZWG":7.231886,"ZWL":7.231886,"KPW":0.0003025};

  var COUNTRY_CURRENCY = {
      AD: "EUR", AE: "AED", AF: "AFN", AG: "XCD", AI: "XCD", AL: "ALL", AM: "AMD",
      AO: "AOA", AR: "ARS", AS: "USD", AT: "EUR", AU: "AUD", AW: "AWG", AX: "EUR",
      AZ: "AZN", BA: "BAM", BB: "BBD", BD: "BDT", BE: "EUR", BF: "XOF", BG: "BGN",
      BH: "BHD", BI: "BIF", BJ: "XOF", BL: "EUR", BM: "BMD", BN: "BND", BO: "BOB",
      BQ: "USD", BR: "BRL", BS: "BSD", BT: "BTN", BV: "NOK", BW: "BWP", BY: "BYN",
      BZ: "BZD", CA: "CAD", CC: "AUD", CD: "CDF", CF: "XAF", CG: "XAF", CH: "CHF",
      CI: "XOF", CK: "NZD", CL: "CLP", CM: "XAF", CN: "CNY", CO: "COP", CR: "CRC",
      CU: "CUP", CV: "CVE", CW: "ANG", CX: "AUD", CY: "EUR", CZ: "CZK", DE: "EUR",
      DJ: "DJF", DK: "DKK", DM: "XCD", DO: "DOP", DZ: "DZD", EC: "USD", EE: "EUR",
      EG: "EGP", EH: "MAD", ER: "ERN", ES: "EUR", ET: "ETB", FI: "EUR", FJ: "FJD",
      FK: "FKP", FM: "USD", FO: "DKK", FR: "EUR", GA: "XAF", GB: "GBP", GD: "XCD",
      GE: "GEL", GF: "EUR", GG: "GBP", GH: "GHS", GI: "GIP", GL: "DKK", GM: "GMD",
      GN: "GNF", GP: "EUR", GQ: "XAF", GR: "EUR", GS: "GBP", GT: "GTQ", GU: "USD",
      GW: "XOF", GY: "GYD", HK: "HKD", HN: "HNL", HR: "EUR", HT: "HTG", HU: "HUF",
      ID: "IDR", IE: "EUR", IL: "ILS", IM: "GBP", IN: "INR", IO: "USD", IQ: "IQD",
      IR: "IRR", IS: "ISK", IT: "EUR", JE: "GBP", JM: "JMD", JO: "JOD", JP: "JPY",
      KE: "KES", KG: "KGS", KH: "KHR", KI: "AUD", KM: "KMF", KN: "XCD", KP: "KPW",
      KR: "KRW", KW: "KWD", KY: "KYD", KZ: "KZT", LA: "LAK", LB: "LBP", LC: "XCD",
      LI: "CHF", LK: "LKR", LR: "LRD", LS: "LSL", LT: "EUR", LU: "EUR", LV: "EUR",
      LY: "LYD", MA: "MAD", MC: "EUR", MD: "MDL", ME: "EUR", MF: "EUR", MG: "MGA",
      MH: "USD", MK: "MKD", ML: "XOF", MM: "MMK", MN: "MNT", MO: "MOP", MP: "USD",
      MQ: "EUR", MR: "MRU", MS: "XCD", MT: "EUR", MU: "MUR", MV: "MVR", MW: "MWK",
      MX: "MXN", MY: "MYR", MZ: "MZN", NA: "NAD", NC: "XPF", NE: "XOF", NF: "AUD",
      NG: "NGN", NI: "NIO", NL: "EUR", NO: "NOK", NP: "NPR", NR: "AUD", NU: "NZD",
      NZ: "NZD", OM: "OMR", PA: "PAB", PE: "PEN", PF: "XPF", PG: "PGK", PH: "PHP",
      PK: "PKR", PL: "PLN", PM: "EUR", PN: "NZD", PR: "USD", PS: "ILS", PT: "EUR",
      PW: "USD", PY: "PYG", QA: "QAR", RE: "EUR", RO: "RON", RS: "RSD", RU: "RUB",
      RW: "RWF", SA: "SAR", SB: "SBD", SC: "SCR", SD: "SDG", SE: "SEK", SG: "SGD",
      SH: "SHP", SI: "EUR", SJ: "NOK", SK: "EUR", SL: "SLE", SM: "EUR", SN: "XOF",
      SO: "SOS", SR: "SRD", SS: "SSP", ST: "STN", SV: "USD", SX: "ANG", SY: "SYP",
      SZ: "SZL", TC: "USD", TD: "XAF", TG: "XOF", TH: "THB", TJ: "TJS", TK: "NZD",
      TL: "USD", TM: "TMT", TN: "TND", TO: "TOP", TR: "TRY", TT: "TTD", TV: "AUD",
      TW: "TWD", TZ: "TZS", UA: "UAH", UG: "UGX", US: "USD", UY: "UYU", UZ: "UZS",
      VA: "EUR", VC: "XCD", VE: "VES", VG: "USD", VI: "USD", VN: "VND", VU: "VUV",
      WF: "XPF", WS: "WST", YE: "YER", YT: "EUR", ZA: "ZAR", ZM: "ZMW", ZW: "ZWL",
      TF: "EUR", HM: "AUD", XK: "EUR", UM: "USD",
    };

  // ISO 3166-1 alpha-2 → English country name (header selector).
  var COUNTRY_NAMES = {"AW":"Aruba","AF":"Afghanistan","AO":"Angola","AI":"Anguilla","AX":"Åland Islands","AL":"Albania","AD":"Andorra","AE":"United Arab Emirates","AR":"Argentina","AM":"Armenia","AS":"American Samoa","AQ":"Antarctica","TF":"French Southern and Antarctic Lands","AG":"Antigua and Barbuda","AU":"Australia","AT":"Austria","AZ":"Azerbaijan","BI":"Burundi","BE":"Belgium","BJ":"Benin","BF":"Burkina Faso","BD":"Bangladesh","BG":"Bulgaria","BH":"Bahrain","BS":"Bahamas","BA":"Bosnia and Herzegovina","BL":"Saint Barthélemy","SH":"Saint Helena, Ascension and Tristan da Cunha","BY":"Belarus","BZ":"Belize","BM":"Bermuda","BO":"Bolivia","BQ":"Caribbean Netherlands","BR":"Brazil","BB":"Barbados","BN":"Brunei","BT":"Bhutan","BV":"Bouvet Island","BW":"Botswana","CF":"Central African Republic","CA":"Canada","CC":"Cocos (Keeling) Islands","CH":"Switzerland","CL":"Chile","CN":"China","CI":"Ivory Coast","CM":"Cameroon","CD":"DR Congo","CG":"Republic of the Congo","CK":"Cook Islands","CO":"Colombia","KM":"Comoros","CV":"Cape Verde","CR":"Costa Rica","CU":"Cuba","CW":"Curaçao","CX":"Christmas Island","KY":"Cayman Islands","CY":"Cyprus","CZ":"Czechia","DE":"Germany","DJ":"Djibouti","DM":"Dominica","DK":"Denmark","DO":"Dominican Republic","DZ":"Algeria","EC":"Ecuador","EG":"Egypt","ER":"Eritrea","EH":"Western Sahara","ES":"Spain","EE":"Estonia","ET":"Ethiopia","FI":"Finland","FJ":"Fiji","FK":"Falkland Islands","FR":"France","FO":"Faroe Islands","FM":"Micronesia","GA":"Gabon","GB":"United Kingdom","GE":"Georgia","GG":"Guernsey","GH":"Ghana","GI":"Gibraltar","GN":"Guinea","GP":"Guadeloupe","GM":"Gambia","GW":"Guinea-Bissau","GQ":"Equatorial Guinea","GR":"Greece","GD":"Grenada","GL":"Greenland","GT":"Guatemala","GF":"French Guiana","GU":"Guam","GY":"Guyana","HK":"Hong Kong","HM":"Heard Island and McDonald Islands","HN":"Honduras","HR":"Croatia","HT":"Haiti","HU":"Hungary","ID":"Indonesia","IM":"Isle of Man","IN":"India","IO":"British Indian Ocean Territory","IE":"Ireland","IR":"Iran","IQ":"Iraq","IS":"Iceland","IL":"Israel","IT":"Italy","JM":"Jamaica","JE":"Jersey","JO":"Jordan","JP":"Japan","KZ":"Kazakhstan","KE":"Kenya","KG":"Kyrgyzstan","KH":"Cambodia","KI":"Kiribati","KN":"Saint Kitts and Nevis","KR":"South Korea","XK":"Kosovo","KW":"Kuwait","LA":"Laos","LB":"Lebanon","LR":"Liberia","LY":"Libya","LC":"Saint Lucia","LI":"Liechtenstein","LK":"Sri Lanka","LS":"Lesotho","LT":"Lithuania","LU":"Luxembourg","LV":"Latvia","MO":"Macau","MF":"Saint Martin","MA":"Morocco","MC":"Monaco","MD":"Moldova","MG":"Madagascar","MV":"Maldives","MX":"Mexico","MH":"Marshall Islands","MK":"North Macedonia","ML":"Mali","MT":"Malta","MM":"Myanmar","ME":"Montenegro","MN":"Mongolia","MP":"Northern Mariana Islands","MZ":"Mozambique","MR":"Mauritania","MS":"Montserrat","MQ":"Martinique","MU":"Mauritius","MW":"Malawi","MY":"Malaysia","YT":"Mayotte","NA":"Namibia","NC":"New Caledonia","NE":"Niger","NF":"Norfolk Island","NG":"Nigeria","NI":"Nicaragua","NU":"Niue","NL":"Netherlands","NO":"Norway","NP":"Nepal","NR":"Nauru","NZ":"New Zealand","OM":"Oman","PK":"Pakistan","PA":"Panama","PN":"Pitcairn Islands","PE":"Peru","PH":"Philippines","PW":"Palau","PG":"Papua New Guinea","PL":"Poland","PR":"Puerto Rico","KP":"North Korea","PT":"Portugal","PY":"Paraguay","PS":"Palestine","PF":"French Polynesia","QA":"Qatar","RE":"Réunion","RO":"Romania","RU":"Russia","RW":"Rwanda","SA":"Saudi Arabia","SD":"Sudan","SN":"Senegal","SG":"Singapore","GS":"South Georgia","SJ":"Svalbard and Jan Mayen","SB":"Solomon Islands","SL":"Sierra Leone","SV":"El Salvador","SM":"San Marino","SO":"Somalia","PM":"Saint Pierre and Miquelon","RS":"Serbia","SS":"South Sudan","ST":"São Tomé and Príncipe","SR":"Suriname","SK":"Slovakia","SI":"Slovenia","SE":"Sweden","SZ":"Eswatini","SX":"Sint Maarten","SC":"Seychelles","SY":"Syria","TC":"Turks and Caicos Islands","TD":"Chad","TG":"Togo","TH":"Thailand","TJ":"Tajikistan","TK":"Tokelau","TM":"Turkmenistan","TL":"Timor-Leste","TO":"Tonga","TT":"Trinidad and Tobago","TN":"Tunisia","TR":"Türkiye","TV":"Tuvalu","TW":"Taiwan","TZ":"Tanzania","UG":"Uganda","UA":"Ukraine","UM":"United States Minor Outlying Islands","UY":"Uruguay","US":"United States","UZ":"Uzbekistan","VA":"Vatican City","VC":"Saint Vincent and the Grenadines","VE":"Venezuela","VG":"British Virgin Islands","VI":"United States Virgin Islands","VN":"Vietnam","VU":"Vanuatu","WF":"Wallis and Futuna","WS":"Samoa","YE":"Yemen","ZA":"South Africa","ZM":"Zambia","ZW":"Zimbabwe"};

  // IANA timezone → country (fallback when IP geolocation is unavailable).
  var TZ_COUNTRY = {
    "Asia/Dubai": "AE", "Asia/Muscat": "OM", "Asia/Riyadh": "SA",
    "Asia/Kuwait": "KW", "Asia/Bahrain": "BH", "Asia/Qatar": "QA",
    "Asia/Amman": "JO", "Asia/Beirut": "LB", "Asia/Damascus": "SY",
    "Asia/Baghdad": "IQ", "Asia/Tehran": "IR", "Asia/Jerusalem": "IL",
    "Asia/Istanbul": "TR", "Europe/Istanbul": "TR", "Asia/Karachi": "PK",
    "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Asia/Dhaka": "BD",
    "Asia/Colombo": "LK", "Asia/Kathmandu": "NP", "Asia/Katmandu": "NP",
    "Asia/Yangon": "MM", "Asia/Rangoon": "MM", "Asia/Bangkok": "TH",
    "Asia/Phnom_Penh": "KH", "Asia/Vientiane": "LA", "Asia/Ho_Chi_Minh": "VN",
    "Asia/Saigon": "VN", "Asia/Kuala_Lumpur": "MY", "Asia/Singapore": "SG",
    "Asia/Jakarta": "ID", "Asia/Manila": "PH", "Asia/Shanghai": "CN",
    "Asia/Urumqi": "CN", "Asia/Hong_Kong": "HK", "Asia/Macau": "MO",
    "Asia/Taipei": "TW", "Asia/Tokyo": "JP", "Asia/Seoul": "KR",
    "Asia/Ulaanbaatar": "MN", "Asia/Almaty": "KZ", "Asia/Tashkent": "UZ",
    "Asia/Ashgabat": "TM", "Asia/Bishkek": "KG", "Asia/Dushanbe": "TJ",
    "Asia/Kabul": "AF", "Asia/Yerevan": "AM", "Asia/Baku": "AZ",
    "Asia/Tbilisi": "GE", "Europe/Moscow": "RU", "Europe/London": "GB",
    "Europe/Dublin": "IE", "Europe/Lisbon": "PT", "Europe/Madrid": "ES",
    "Europe/Paris": "FR", "Europe/Amsterdam": "NL", "Europe/Brussels": "BE",
    "Europe/Berlin": "DE", "Europe/Vienna": "AT", "Europe/Zurich": "CH",
    "Europe/Rome": "IT", "Europe/Stockholm": "SE", "Europe/Oslo": "NO",
    "Europe/Copenhagen": "DK", "Europe/Helsinki": "FI", "Europe/Warsaw": "PL",
    "Europe/Prague": "CZ", "Europe/Budapest": "HU", "Europe/Bucharest": "RO",
    "Europe/Sofia": "BG", "Europe/Belgrade": "RS", "Europe/Athens": "GR",
    "Europe/Kyiv": "UA", "Europe/Kiev": "UA", "America/New_York": "US",
    "America/Chicago": "US", "America/Denver": "US", "America/Los_Angeles": "US",
    "America/Phoenix": "US", "America/Anchorage": "US", "America/Toronto": "CA",
    "America/Vancouver": "CA", "America/Montreal": "CA", "America/Mexico_City": "MX",
    "America/Bogota": "CO", "America/Lima": "PE", "America/Santiago": "CL",
    "America/Buenos_Aires": "AR", "America/Sao_Paulo": "BR", "America/Caracas": "VE",
    "America/Panama": "PA", "Australia/Sydney": "AU", "Australia/Melbourne": "AU",
    "Australia/Brisbane": "AU", "Australia/Perth": "AU", "Pacific/Auckland": "NZ",
    "Africa/Cairo": "EG", "Africa/Casablanca": "MA", "Africa/Johannesburg": "ZA",
    "Africa/Nairobi": "KE", "Africa/Lagos": "NG", "Africa/Accra": "GH",
    "Africa/Addis_Ababa": "ET", "Africa/Tunis": "TN", "Africa/Algiers": "DZ",
    "Pacific/Honolulu": "US", "Pacific/Guam": "GU", "Asia/Dili": "TL",
    "Indian/Maldives": "MV", "Indian/Mauritius": "MU",
  };

  // ISO 4217 minor units: 0- and 3-decimal currencies (default 2).
  var DECIMALS = {
    BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
    BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0,
    PYG: 0, RWF: 0, UGX: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  };

  // Sellable countries from Shopify Markets (window.NudayMarkets, rendered
  // from localization.available_countries in theme.liquid). Markets only
  // decide WHICH countries the currency selector offers; every country is
  // displayed and converted at its own official currency (COUNTRY_CURRENCY),
  // not the currency of the market it belongs to. Falls back to the
  // hardcoded full COUNTRY_NAMES/COUNTRY_CURRENCY tables when the global is
  // missing/empty.
  var MARKETS = null;
  (function () {
    var raw = window.NudayMarkets;
    if (!Array.isArray(raw) || !raw.length) return;
    var list = [];
    var seen = {};
    for (var i = 0; i < raw.length; i++) {
      var m = raw[i];
      if (!m) continue;
      var code = String(m.code || "").toUpperCase();
      if (!/^[A-Za-z]{2}$/.test(code) || seen[code]) continue;
      seen[code] = true;
      list.push({ code: code, name: String(m.name || code) });
    }
    if (list.length) MARKETS = list;
  })();

  function inMarkets(code) {
    for (var i = 0; i < MARKETS.length; i++) {
      if (MARKETS[i].code === code) return true;
    }
    return false;
  }

  // Currency shown/converted for a country: always the country's own
  // currency. In markets mode only sellable countries convert at all.
  function marketCurrency(code) {
    if (MARKETS && !inMarkets(code)) return null;
    return COUNTRY_CURRENCY[code] || null;
  }

  function marketName(code) {
    if (MARKETS) {
      for (var i = 0; i < MARKETS.length; i++) {
        if (MARKETS[i].code === code) return MARKETS[i].name;
      }
    }
    return COUNTRY_NAMES[code] || code;
  }

  // Is this country offered in the currency dropdown?
  function isSelectable(code) {
    if (!code) return false;
    if (MARKETS) return marketCurrency(code) != null;
    return COUNTRY_NAMES[code] != null && COUNTRY_CURRENCY[code] != null;
  }

  // (amount element, sibling currency-label element) pairs per price surface.
  var PAIRS = [
    [".price-item__number", ".price-item__currency"],
    [".custom-card-amount", ".custom-card-currency"],
    [".cart-item__price-number", ".cart-item__price-currency"],
    [".bundle-card__amount", ".bundle-card__currency"],
    [".byo__pcard-amount", ".byo__pcard-currency"],
    [".cf-prices__amount", ".cf-prices__currency"],
    [".bundle-selector__price-number", ".bundle-selector__price-currency"],
    [".shop-by-goal__price-amount", ".shop-by-goal__price-currency"],
    [".hero__product-price-amount", ".hero__product-price-currency"],
    [".crosssell__price-number", ".crosssell__price-currency"],
    [".price-number", ".price-currency"],
    [".totals__total-number", ".totals__total-currency"],
    [".ux-suggested-item__amount", ".ux-suggested-item__currency"],
  ];
  // Elements holding a full "AED 88.00" string (money / money_with_currency).
  var PLAIN = [
    ".variant-item__old-price",
    // Holds the slide product's price on the mobile hero (merchant copy there
    // does not match the "AED <amount>" shape, so it is left alone).
    ".hero__heading",
  ];
  // Free-shipping progress milestones ("250", "AED 400", "AED 600"…).
  var THRESHOLDS = [".cart-progressBar__item-price"];
  // Amount-only elements whose label sits in a sibling .byo__currency span.
  var BYO_TOTALS = [
    "[data-byo-item-total]",
    "[data-byo-stack-discount]",
    "[data-byo-sub-benefit]",
    "[data-byo-grand]",
  ];
  // Label-only spans (rewritten to the target code, "- " prefix preserved).
  var LABELS = [".byo__currency"];
  // Mixed text with inline amounts, e.g. "You save AED 12.00 today".
  var MIXED = [
    "[data-byo-save-text]",
    ".cart-progressBar__title",
    ".ux-shipping-notice__title",
    ".find-your-stacks-subscription-perks__support-text",
    ".custom-card-daily-price",
    ".bundle-selector__per-pack",
  ];

  var target = null; // target ISO code
  var rate = null;   // SHOP_CURRENCY → target rate
  // Theme setting "Auto-detect currency by country" gates everything.
  var FX_ENABLED = window.NudayFXEnabled === true;

  // ── helpers ────────────────────────────────────────────────────────────────

  function fetchJSON(url, timeoutMs) {
    var ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, timeoutMs || 8000) : null;
    return fetch(url, { signal: ctrl ? ctrl.signal : undefined })
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function () { return null; })
      .finally(function () { if (timer) clearTimeout(timer); });
  }

  function storageGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function storageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function decimalsOf(code) {
    return DECIMALS[code] != null ? DECIMALS[code] : 2;
  }

  function parseCents(text) {
    var n = parseFloat(String(text == null ? "" : text).replace(/[^0-9.-]/g, ""));
    return isNaN(n) ? null : Math.round(n * 100);
  }

  function formatAmount(aedCents, code) {
    var converted = Math.round(aedCents * rate);
    var dec = decimalsOf(code);
    var nf = new Intl.NumberFormat("en", {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
    return nf.format(converted / 100);
  }

  // ── header selector ────────────────────────────────────────────────────────

  function flagEmoji(code) {
    if (!/^[A-Za-z]{2}$/.test(code)) return "";
    var out = "";
    for (var i = 0; i < 2; i++) {
      out += String.fromCodePoint(0x1f1e6 + code.toUpperCase().charCodeAt(i) - 65);
    }
    return out;
  }

  // Header + menu-drawer instances (mobile shows the drawer copy).
  function getSelectors() {
    return document.querySelectorAll("[data-nuday-currency]");
  }

  // Closed state shows only "flag + currency code"; the open list shows the
  // full "flag + country name + the country's own currency code" for every
  // market country. The menu drawer label (nuday-currency-label--country)
  // shows the full name too.
  function setSelectorLabel(root, code) {
    if (!root || !code) return;
    var label = root.querySelector("[data-nuday-currency-label]");
    var list = root.querySelector("[data-nuday-currency-list]");
    var cur = marketCurrency(code) || COUNTRY_CURRENCY[code];
    var name = marketName(code);
    if (!label) return;
    if (label.classList.contains("nuday-currency-label--country")) {
      label.textContent = flagEmoji(code) + " " + name + " (" + (cur || code) + ")";
    } else {
      label.textContent = flagEmoji(code) + " " + (cur || code);
    }
    label.dataset.code = code;
    if (list) {
      var items = list.querySelectorAll("[data-value]");
      for (var i = 0; i < items.length; i++) {
        var active = items[i].dataset.value === code;
        items[i].setAttribute("aria-selected", active ? "true" : "false");
        if (active) items[i].classList.add("is-active");
        else items[i].classList.remove("is-active");
      }
    }
  }

  function wireSelector() {
    var roots = getSelectors();
    if (!roots || !roots.length) return;

    // Build the list from the store's market countries only (flag + country
    // name + the country's own currency), alphabetical by name. When no
    // market data is exposed, fall back to every known country.
    var entries;
    if (MARKETS) {
      entries = [];
      for (var m = 0; m < MARKETS.length; m++) {
        var mk = MARKETS[m];
        var mkCur = COUNTRY_CURRENCY[mk.code];
        if (!mkCur) continue; // no official currency (e.g. Antarctica)
        entries.push({ code: mk.code, name: mk.name, currency: mkCur });
      }
    } else {
      entries = [];
      var allCodes = Object.keys(COUNTRY_NAMES);
      for (var a = 0; a < allCodes.length; a++) {
        var legacyCode = allCodes[a];
        var legacyCur = COUNTRY_CURRENCY[legacyCode];
        if (!legacyCur) continue; // no official currency (e.g. Antarctica)
        entries.push({ code: legacyCode, name: COUNTRY_NAMES[legacyCode], currency: legacyCur });
      }
    }
    entries.sort(function (x, y) {
      return x.name.localeCompare(y.name);
    });
    var frag = document.createDocumentFragment();
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "nuday-currency-option";
      btn.dataset.value = entry.code;
      btn.textContent = flagEmoji(entry.code) + " " + entry.name + " (" + entry.currency + ")";
      frag.appendChild(btn);
    }

    // Wire one instance; separate call per root so closures capture their own
    // trigger/list instead of the loop's shared var bindings.
    function wireOne(root) {
      var trigger = root.querySelector("[data-nuday-currency-trigger]");
      var list = root.querySelector("[data-nuday-currency-list]");
      if (!trigger || !list) return;

      list.appendChild(frag.cloneNode(true));

      // Preselect: saved choice, else the shop's market country from Liquid.
      // A saved country that is no longer sellable falls back to the default.
      var pick = storageGet(LS_COUNTRY) || root.getAttribute("data-default-country") || "AE";
      if (!isSelectable(pick)) {
        pick = root.getAttribute("data-default-country") || "AE";
        if (!isSelectable(pick) && entries.length) pick = entries[0].code;
      }
      setSelectorLabel(root, pick);

      function openList() {
        list.hidden = false;
        trigger.setAttribute("aria-expanded", "true");
        var active = list.querySelector(".is-active") || list.firstElementChild;
        if (active) active.focus();
      }
      function closeList(refocus) {
        list.hidden = true;
        trigger.setAttribute("aria-expanded", "false");
        if (refocus) trigger.focus();
      }

      trigger.addEventListener("click", function () {
        if (list.hidden) openList();
        else closeList(false);
      });
      list.addEventListener("click", function (e) {
        var picked = e.target.closest ? e.target.closest("[data-value]") : null;
        if (!picked) return;
        storageSet(LS_COUNTRY, picked.dataset.value);
        window.location.reload();
      });
      document.addEventListener("click", function (e) {
        if (!list.hidden && !root.contains(e.target)) closeList(false);
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !list.hidden) closeList(true);
      });
    }

    for (var r = 0; r < roots.length; r++) {
      wireOne(roots[r]);
    }
  }

  function syncSelector(country) {
    var roots = getSelectors();
    if (!roots || !roots.length || !country) return;
    for (var i = 0; i < roots.length; i++) {
      setSelectorLabel(roots[i], country);
    }
  }

  // ── country detection ──────────────────────────────────────────────────────

  function timezoneCountry() {
    try {
      var tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return (tz && TZ_COUNTRY[tz]) || null;
    } catch (e) { return null; }
  }

  function detectCountry() {
    // 1. Explicit selection via the country/localization selector.
    var saved = storageGet(LS_COUNTRY);
    if (saved) return Promise.resolve(saved);

    // 2. IP geolocation (CORS-enabled free endpoint).
    return fetchJSON("https://ipapi.co/json/", 5000)
      .then(function (d) {
        if (d && /^[A-Za-z]{2}$/.test(d.country_code || "")) return d.country_code.toUpperCase();
        return timezoneCountry();
      })
      .catch(function () { return timezoneCountry(); });
  }

  // ── rates ──────────────────────────────────────────────────────────────────

  function normalizeErApi(d) {
    if (d && d.result === "success" && d.rates) return d.rates;
    return null;
  }

  function normalizeJsdelivr(d) {
    var key = SHOP_CURRENCY.toLowerCase();
    if (d && d[key] && typeof d[key] === "object") {
      var out = {};
      Object.keys(d[key]).forEach(function (k) { out[k.toUpperCase()] = d[key][k]; });
      return out;
    }
    return null;
  }

  function fetchRates() {
    return fetchJSON(RATES_URLS[0], 8000)
      .then(normalizeErApi)
      .then(function (rates) {
        return rates ? rates : fetchJSON(RATES_URLS[1], 8000).then(normalizeJsdelivr);
      });
  }

  function loadRates() {
    var cached = null;
    try { cached = JSON.parse(storageGet(LS_RATES) || "null"); } catch (e) {}
    if (cached && cached.ts && Date.now() - cached.ts < RATES_TTL && cached.rates) {
      return Promise.resolve(cached.rates);
    }
    return fetchRates().then(function (rates) {
      if (rates && Object.keys(rates).length) {
        storageSet(LS_RATES, JSON.stringify({ ts: Date.now(), rates: rates }));
        return rates;
      }
      return SNAPSHOT_RATES;
    });
  }

  // ── DOM conversion ─────────────────────────────────────────────────────────

  function convertAmountEl(el, labelSel) {
    if (!el || el.dataset.fxAed) return;
    var aedCents = parseCents(el.textContent);
    if (aedCents == null) return;
    var out = formatAmount(aedCents, target);
    el.dataset.fxAed = String(aedCents);
    el.dataset.fxAedOut = out;
    el.textContent = out;
    if (labelSel && el.parentElement) {
      var label = el.parentElement.querySelector(labelSel);
      if (label) setLabel(label);
    }
  }

  function convertPlainEl(el) {
    if (!el || el.dataset.fxAed) return;
    var m = String(el.textContent).match(/^\s*(?:AED|Dhs\.?|د\.إ)\s*([\d.,]+)\s*$/i);
    if (!m) return;
    var aedCents = parseCents(m[1]);
    if (aedCents == null) return;
    var out = target + " " + formatAmount(aedCents, target);
    el.dataset.fxAed = String(aedCents);
    el.dataset.fxAedOut = out;
    el.textContent = out;
  }

  function convertThresholdEl(el) {
    // Milestones may render bare ("250") or prefixed ("AED 400"); always
    // output "TARGET amount" so all three stay consistent with the title.
    if (!el || el.dataset.fxAed) return;
    var m = String(el.textContent).match(/^\s*(?:AED|Dhs\.?|د\.إ)?\s*([\d.,]+)\s*$/i);
    if (!m) return;
    var aedCents = parseCents(m[1]);
    if (aedCents == null) return;
    var out = target + " " + formatAmount(aedCents, target);
    el.dataset.fxAed = String(aedCents);
    el.dataset.fxAedOut = out;
    el.textContent = out;
  }

  function convertMixedEl(el) {
    if (!el || el.dataset.fxAed) return;
    var out = el.textContent.replace(/\b(?:AED|Dhs\.?)\s?([\d.,]+)\b/gi, function (full, num) {
      var aedCents = parseCents(num);
      if (aedCents == null) return full;
      return target + " " + formatAmount(aedCents, target);
    });
    if (out !== el.textContent) {
      el.dataset.fxAed = "1";
      el.dataset.fxAedOut = out;
      el.textContent = out;
    }
  }

  function setLabel(el) {
    if (!el) return;
    // Preserve a leading "- " prefix (e.g. "- AED" discount rows).
    var prefix = /^(\s*-\s*)/.exec(el.textContent);
    var out = (prefix ? prefix[1] : "") + target;
    if (el.textContent === out) return;
    el.textContent = out;
  }

  function convert(root) {
    if (!rate || !target) return;
    var i, j;
    for (i = 0; i < PAIRS.length; i++) {
      var amountSel = PAIRS[i][0];
      var labelSel = PAIRS[i][1];
      // MutationObserver delivers the leaf node itself — match root too.
      if (root.matches && root.matches(amountSel)) convertAmountEl(root, labelSel);
      var els = root.querySelectorAll(amountSel);
      for (j = 0; j < els.length; j++) convertAmountEl(els[j], labelSel);
    }
    for (i = 0; i < PLAIN.length; i++) {
      if (root.matches && root.matches(PLAIN[i])) convertPlainEl(root);
      var plain = root.querySelectorAll(PLAIN[i]);
      for (j = 0; j < plain.length; j++) convertPlainEl(plain[j]);
    }
    for (i = 0; i < THRESHOLDS.length; i++) {
      if (root.matches && root.matches(THRESHOLDS[i])) convertThresholdEl(root);
      var th = root.querySelectorAll(THRESHOLDS[i]);
      for (j = 0; j < th.length; j++) convertThresholdEl(th[j]);
    }
    for (i = 0; i < BYO_TOTALS.length; i++) {
      if (root.matches && root.matches(BYO_TOTALS[i])) convertAmountEl(root, ".byo__currency");
      var byo = root.querySelectorAll(BYO_TOTALS[i]);
      for (j = 0; j < byo.length; j++) convertAmountEl(byo[j], ".byo__currency");
    }
    for (i = 0; i < LABELS.length; i++) {
      if (root.matches && root.matches(LABELS[i])) setLabel(root);
      var labels = root.querySelectorAll(LABELS[i]);
      for (j = 0; j < labels.length; j++) setLabel(labels[j]);
    }
    for (i = 0; i < MIXED.length; i++) {
      if (root.matches && root.matches(MIXED[i])) convertMixedEl(root);
      var mixed = root.querySelectorAll(MIXED[i]);
      for (j = 0; j < mixed.length; j++) convertMixedEl(mixed[j]);
    }
  }

  // ── init ───────────────────────────────────────────────────────────────────

  function init() {
    if (!FX_ENABLED) return;
    wireSelector();
    detectCountry().then(function (country) {
      if (!country) return;
      var countryCode = String(country).toUpperCase();
      // Outside the store's markets there is nothing to convert to — keep the
      // shop's default market currency and don't move the selector.
      if (!isSelectable(countryCode)) return;
      syncSelector(countryCode);
      var code = marketCurrency(countryCode);
      if (!code || code === SHOP_CURRENCY) return;
      target = code;
      loadRates().then(function (rates) {
        // Live rates can omit rare currencies (e.g. KPW) — fall back to the
        // embedded snapshot so the chosen country's currency always applies.
        rate =
          (rates && rates[code] != null)
            ? Number(rates[code])
            : SNAPSHOT_RATES[code] != null
              ? SNAPSHOT_RATES[code]
              : null;
        if (!rate || rate === 1) { target = null; return; }
        convert(document);
        // A text-level mutation on a converted element means another script
        // rewrote the price (e.g. renderCart recalculating totals). If the
        // current text no longer matches our converted output, drop the anchor
        // so convert() re-runs; identical text is our own write — skip.
        function textChanged(el) {
          if (!el || !el.dataset || el.dataset.fxAedOut == null) return false;
          return String(el.textContent).trim() !== el.dataset.fxAedOut;
        }
        function convertTextParent(p) {
          if (!p) return;
          if (textChanged(p)) { delete p.dataset.fxAed; delete p.dataset.fxAedOut; }
          convert(p);
        }
        var mo = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            var added = mutations[i].addedNodes;
            for (var j = 0; j < added.length; j++) {
              if (added[j].nodeType === 1) {
                convert(added[j]);
              } else if (added[j].nodeType === 3 && added[j].parentElement) {
                // textContent swaps add a fresh text node — convert its parent.
                convertTextParent(added[j].parentElement);
              }
            }
            // Text-level updates (in-place cart total recalculations etc.).
            if (mutations[i].type === "characterData" && mutations[i].target && mutations[i].target.parentElement) {
              convertTextParent(mutations[i].target.parentElement);
            }
          }
        });
        mo.observe(document.body, { childList: true, subtree: true, characterData: true });
      });
    });
  }

  // Remember an explicit country picked in the header/footer country selector.
  document.addEventListener("submit", function (e) {
    var form = e.target && e.target.closest ? e.target.closest("form") : null;
    if (!form) return;
    var input = form.querySelector('input[name="country_code"]');
    if (input && input.value) storageSet(LS_COUNTRY, input.value.toUpperCase());
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
